import React from "react";
import { Modal } from "reactstrap";
import moment from "moment";
import { TRAVELER_TYPE_LABELS, METHOD_LABELS } from "./state";

const styles = {
  sectionTitle: {
    fontSize: "0.78rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#64748b",
    margin: "18px 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionBody: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "12px 24px",
  },
  kvLabel: { fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", marginBottom: 2 },
  kvValue: { fontSize: "0.9rem", color: "#0f172a", wordBreak: "break-word" },
};

const formatDate = (d, fmt = "DD-MMM-YYYY") => {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format(fmt) : "—";
};

const KV = ({ label, value }) => (
  <div>
    <div style={styles.kvLabel}>{label}</div>
    <div style={styles.kvValue}>{value != null && String(value).trim() !== "" ? value : "—"}</div>
  </div>
);

const Section = ({ icon, title, children }) => (
  <div>
    <div style={styles.sectionTitle}>
      <i className={icon} style={{ fontSize: "1rem", color: "#0ea5e9" }} />
      {title}
    </div>
    <div style={styles.sectionBody}>
      <div style={styles.kvGrid}>{children}</div>
    </div>
  </div>
);

/**
 * Everything a passenger submitted on the Customer Information form.
 * `hideDuplicates` leaves out the name/flight fields the case file already carries.
 */
export const CustomerInfoDetails = ({ info, hideDuplicates = false }) => {
  if (!info) return null;
  const isTourist = info.traveler_type === "TOURIST";
  const methods = (info.contact_methods || []).map((m) => METHOD_LABELS[m] || m).join(", ");

  return (
    <div>
      <Section icon="mdi mdi-card-account-details-outline" title="Submission">
        <KV label="Reference" value={info.reference} />
        <KV label="Submitted" value={formatDate(info.created_at, "DD-MMM-YYYY HH:mm")} />
        <KV label="Traveler type" value={TRAVELER_TYPE_LABELS[info.traveler_type] || info.traveler_type} />
        {!hideDuplicates && (
          <>
            <KV label="Full name" value={info.full_name} />
            <KV label="Arrival flight" value={info.arrival_flight} />
            <KV label="Arrival date" value={formatDate(info.arrival_date)} />
          </>
        )}
      </Section>

      {isTourist && (
        <Section icon="mdi mdi-bed-outline" title="Stay & return">
          <KV label="Place of stay" value={info.place_of_stay} />
          <KV label="Hotel contact" value={info.hotel_contact} />
          <KV label="Return flight" value={info.return_flight} />
          <KV label="Return date" value={formatDate(info.return_date)} />
        </Section>
      )}

      <Section icon="mdi mdi-home-outline" title="Home address">
        <KV label="Address" value={info.address} />
        <KV label="Road / street" value={info.road} />
        <KV label="Zip / postal" value={info.zip} />
        <KV label="District / city / island" value={info.city} />
        <KV label="Country" value={info.country} />
      </Section>

      <Section icon="mdi mdi-phone-outline" title="Contact">
        <KV label="Email" value={info.email} />
        <KV label="Mobile" value={info.mobile} />
        <KV label="Preferred contact" value={methods} />
      </Section>
    </div>
  );
};

/** Popup shown when the linked reference is clicked. */
export const CustomerInfoModal = ({ info, isOpen, toggle }) => (
  <Modal size="lg" isOpen={isOpen} toggle={toggle} scrollable>
    <div className="modal-header">
      <h5 className="modal-title mt-0">Customer Info {info?.reference ? `| ${info.reference}` : ""}</h5>
      <button type="button" onClick={toggle} className="close" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div className="modal-body">
      <CustomerInfoDetails info={info} />
    </div>
    <div className="modal-footer">
      <button type="button" onClick={toggle} className="btn btn-secondary waves-effect">Close</button>
    </div>
  </Modal>
);
